import { inspectionDB } from '../../data/database.structure';
import {
  ObservableLevelsEnum,
  PartCategory,
  PartName,
  TaskStatus,
} from '../../data/enums';
import progressIndicators from '../../data/local-files/progress-indicators';
import { EquipmentModel, InspectionModel } from '../../data/types';
import { InspectionViewModel } from '../../data/viewmodels.types';
import { ProgressIndicatorService } from '../../services/progress-indicator.service';

interface IndicatorsResult {
  sideFrameIndicator: TaskStatus;
  pitchExtensionIndicator: TaskStatus;
  shoeRollerPathIndicator: TaskStatus;
  shoeDriveLugIndicator: TaskStatus;
  tumblerRollerPathIndicator?: TaskStatus;
  tumblerDriveLugIndicator: TaskStatus;
  engagementIndicator: TaskStatus;
  frontIdlerIndicator?: TaskStatus;
  rearIdlerIndicator?: TaskStatus;
  loadRollersIndicator?: TaskStatus;
  returnRollersIndicator?: TaskStatus;
  slidersIndicator?: TaskStatus;
}

interface RollerAssembliesIndicator {
  equipmentPartExternalId: string;
  status: TaskStatus;
  position: number;
  type: 'LoadRoller' | 'ReturnRoller' | 'Slider';
}

class InspectionUndercarriageSideScreenService {
  constructor(
    private inspection: InspectionViewModel,
    private equipment: EquipmentModel,
  ) {}

  private rollersIndicator(rollers: RollerAssembliesIndicator[]) {
    const statuses = rollers.map((r) => r.status);

    // 1) Empty OR all not started → not started
    if (
      statuses.length === 0 ||
      statuses.every((s) => s === TaskStatus.notStarted)
    ) {
      return TaskStatus.notStarted;
    }

    // 2) All completed → completed
    if (statuses.every((s) => s === TaskStatus.completed)) {
      return TaskStatus.completed;
    }

    // 3) Otherwise, at least one in progress or completed → in progress
    return TaskStatus.inProgress;
  }

  public getRollerAssembliesIndicators(): RollerAssembliesIndicator[] {
    const service = new ProgressIndicatorService(
      inspectionDB,
      this.inspection.inspection,
    );

    const loadRollers = this.equipment.equipmentParts.filter(
      (part) =>
        part.part.name === PartName.loadRoller &&
        part.part.partCategory === PartCategory.roller,
    );
    var loadRollersIndicator: RollerAssembliesIndicator[] | undefined =
      undefined;
    if (loadRollers.length > 0) {
      loadRollersIndicator = loadRollers.map((roller) => {
        const status = service.processScreen({
          screen:
            progressIndicators.Root.Undercarriage.children.Side.children
              .LoadRollers,
          assetExternalId: this.inspection.asset?.externalId,
          dataLevel: ObservableLevelsEnum.equipmentPart,
          entityExternalId: roller.externalId,
        });
        return {
          equipmentPartExternalId: roller.externalId,
          status,
          position: roller.position,
          type: 'LoadRoller',
        };
      });
    } else {
      loadRollersIndicator = undefined;
    }

    const returnRollers = this.equipment.equipmentParts.filter(
      (part) =>
        part.part.name === PartName.returnRoller &&
        part.part.partCategory === PartCategory.roller,
    );
    var returnRollersIndicator: RollerAssembliesIndicator[] | undefined =
      undefined;
    if (returnRollers.length > 0) {
      returnRollersIndicator = returnRollers.map((roller) => {
        const status = service.processScreen({
          screen:
            progressIndicators.Root.Undercarriage.children.Side.children
              .ReturnRollers,
          assetExternalId: this.inspection.asset?.externalId,
          dataLevel: ObservableLevelsEnum.equipmentPart,
          entityExternalId: roller.externalId,
        });
        return {
          equipmentPartExternalId: roller.externalId,
          status,
          position: roller.position,
          type: 'ReturnRoller',
        };
      });
    } else {
      returnRollersIndicator = undefined;
    }

    const sliders = this.equipment.equipmentParts.filter(
      (part) =>
        part.part.name === PartName.slider &&
        part.part.partCategory === PartCategory.slider,
    );
    var slidersIndicator: RollerAssembliesIndicator[] | undefined = undefined;
    if (sliders.length > 0) {
      slidersIndicator = sliders.map((roller) => {
        const status = service.processScreen({
          screen:
            progressIndicators.Root.Undercarriage.children.Side.children
              .Sliders,
          assetExternalId: this.inspection.asset?.externalId,
          dataLevel: ObservableLevelsEnum.equipmentPart,
          entityExternalId: roller.externalId,
        });
        return {
          equipmentPartExternalId: roller.externalId,
          status,
          position: roller.position,
          type: 'Slider',
        };
      });
    } else {
      slidersIndicator = undefined;
    }

    return [
      ...(loadRollersIndicator || []),
      ...(returnRollersIndicator || []),
      ...(slidersIndicator || []),
    ];
  }

  public getSideIndicators(): IndicatorsResult {
    const service = new ProgressIndicatorService(
      inspectionDB,
      this.inspection.inspection,
    );

    const sideFrame =
      progressIndicators.Root.Undercarriage.children.Side.children.SideFrame;
    const sideFrameIndicator = service.processScreen({
      screen: sideFrame,
      assetExternalId: this.inspection.asset?.externalId,
      dataLevel: ObservableLevelsEnum.equipment,
      entityExternalId: this.equipment.externalId,
    });

    const pitchExtension = this.equipment.equipmentParts.find(
      (part) => part.part.name === PartName.pitchExtension,
    );
    var pitchExtensionIndicator = TaskStatus.notStarted;
    if (pitchExtension) {
      pitchExtensionIndicator = service.processScreen({
        screen:
          progressIndicators.Root.Undercarriage.children.Side.children
            .PitchExtension,
        assetExternalId: this.inspection.asset?.externalId,
        dataLevel: ObservableLevelsEnum.equipmentPart,
        entityExternalId: pitchExtension.externalId,
      });
    }

    const shoeRollerPath = this.equipment.equipmentParts.find(
      (part) =>
        part.part.name === PartName.rollerPath &&
        part.part.partCategory === PartCategory.shoes,
    );
    var shoeRollerPathIndicator = TaskStatus.notStarted;
    if (shoeRollerPath) {
      shoeRollerPathIndicator = service.processScreen({
        screen:
          progressIndicators.Root.Undercarriage.children.Side.children
            .ShoeRollerPath,
        assetExternalId: this.inspection.asset?.externalId,
        dataLevel: ObservableLevelsEnum.equipmentPart,
        entityExternalId: shoeRollerPath.externalId,
      });
    }

    const shoeDriveLug = this.equipment.equipmentParts.find(
      (part) =>
        part.part.name === PartName.driveLug &&
        part.part.partCategory === PartCategory.shoes,
    );
    var shoeDriveLugIndicator = TaskStatus.notStarted;
    if (shoeDriveLug) {
      shoeDriveLugIndicator = service.processScreen({
        screen:
          progressIndicators.Root.Undercarriage.children.Side.children
            .ShoeDriveLug,
        assetExternalId: this.inspection.asset?.externalId,
        dataLevel: ObservableLevelsEnum.equipmentPart,
        entityExternalId: shoeDriveLug.externalId,
      });
    }

    const hasTumblerRollerPath =
      this.inspection.asset?.model.hasTumblerRollPathMeasurement ||
      this.inspection.asset?.model.assetType === 'HEX';
    var tumblerRollerPathIndicator: TaskStatus | undefined = undefined;
    if (hasTumblerRollerPath) {
      const tumblerRollerPath = this.equipment.equipmentParts.find(
        (part) =>
          part.part.name === PartName.rollerPath &&
          part.part.partCategory === PartCategory.driveTumbler,
      );
      if (tumblerRollerPath) {
        tumblerRollerPathIndicator = service.processScreen({
          screen:
            progressIndicators.Root.Undercarriage.children.Side.children
              .TumblerRollerPath,
          assetExternalId: this.inspection.asset?.externalId,
          dataLevel: ObservableLevelsEnum.equipmentPart,
          entityExternalId: tumblerRollerPath.externalId,
        });
      }
    }

    const tumblerDriveLug = this.equipment.equipmentParts.find(
      (part) =>
        part.part.name === PartName.driveLug &&
        part.part.partCategory === PartCategory.driveTumbler,
    );
    var tumblerDriveLugIndicator = TaskStatus.notStarted;
    if (tumblerDriveLug) {
      tumblerDriveLugIndicator = service.processScreen({
        screen:
          progressIndicators.Root.Undercarriage.children.Side.children
            .TumblerDriveLug,
        assetExternalId: this.inspection.asset?.externalId,
        dataLevel: ObservableLevelsEnum.equipmentPart,
        entityExternalId: tumblerDriveLug.externalId,
      });
    }

    const engagement = this.equipment.equipmentParts.find(
      (part) =>
        part.part.name === PartName.tumblerEngagement &&
        part.part.partCategory === PartCategory.driveTumbler,
    );
    var engagementIndicator = TaskStatus.notStarted;
    if (engagement) {
      engagementIndicator = service.processScreen({
        screen:
          progressIndicators.Root.Undercarriage.children.Side.children
            .Engagement,
        assetExternalId: this.inspection.asset?.externalId,
        dataLevel: ObservableLevelsEnum.equipmentPart,
        entityExternalId: engagement.externalId,
      });
    }

    const frontIdler = this.equipment.equipmentParts.find(
      (part) =>
        part.part.name === PartName.frontIdler &&
        part.part.partCategory === PartCategory.idler,
    );
    var frontIdlerIndicator: TaskStatus | undefined;
    if (frontIdler) {
      frontIdlerIndicator = service.processScreen({
        screen:
          progressIndicators.Root.Undercarriage.children.Side.children
            .FrontIdler,
        assetExternalId: this.inspection.asset?.externalId,
        dataLevel: ObservableLevelsEnum.equipmentPart,
        entityExternalId: frontIdler.externalId,
      });
    } else {
      frontIdlerIndicator = undefined;
    }

    const rearIdler = this.equipment.equipmentParts.find(
      (part) =>
        part.part.name === PartName.rearIdler &&
        part.part.partCategory === PartCategory.idler,
    );
    var rearIdlerIndicator: TaskStatus | undefined;
    if (rearIdler) {
      rearIdlerIndicator = service.processScreen({
        screen:
          progressIndicators.Root.Undercarriage.children.Side.children
            .RearIdler,
        assetExternalId: this.inspection.asset?.externalId,
        dataLevel: ObservableLevelsEnum.equipmentPart,
        entityExternalId: rearIdler.externalId,
      });
    } else {
      rearIdlerIndicator = undefined;
    }

    // Roller Assemblies
    const rollerAssembliesIndicators = this.getRollerAssembliesIndicators();

    const loadRollersIndicators = rollerAssembliesIndicators.filter(
      (roller) => roller.type === 'LoadRoller',
    );
    var loadRollersIndicator: TaskStatus | undefined = undefined;
    if (loadRollersIndicators.length > 0) {
      loadRollersIndicator = this.rollersIndicator(loadRollersIndicators);
    }

    const returnRollersIndicators = rollerAssembliesIndicators.filter(
      (roller) => roller.type === 'ReturnRoller',
    );
    var returnRollersIndicator: TaskStatus | undefined = undefined;
    if (returnRollersIndicators.length > 0) {
      returnRollersIndicator = this.rollersIndicator(returnRollersIndicators);
    }

    const slidersIndicators = rollerAssembliesIndicators.filter(
      (roller) => roller.type === 'Slider',
    );
    var slidersIndicator: TaskStatus | undefined = undefined;
    if (slidersIndicators.length > 0) {
      slidersIndicator = this.rollersIndicator(slidersIndicators);
    }

    return {
      sideFrameIndicator,
      pitchExtensionIndicator,
      shoeRollerPathIndicator,
      shoeDriveLugIndicator,
      tumblerRollerPathIndicator,
      tumblerDriveLugIndicator,
      engagementIndicator,
      frontIdlerIndicator,
      rearIdlerIndicator,
      loadRollersIndicator: loadRollersIndicator,
      returnRollersIndicator: returnRollersIndicator,
      slidersIndicator: slidersIndicator,
    };
  }
}

export default InspectionUndercarriageSideScreenService;
